import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Loader2, Plus, Trash2, Save, X } from "lucide-react";

const empty = { slug: "", name: "", desc: "", params: "", size: "small", tag: "", image: "", order: 0 };

const field =
  "w-full mt-2 border-0 border-b border-slate-300 px-0 py-2.5 bg-transparent focus:outline-none focus:border-teal text-navy text-sm";
const labelClass = "text-[10px] uppercase tracking-[0.2em] text-slate-500";

const PackageForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...empty, ...initial, tag: initial?.tag || "", image: initial?.image || "" });
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name || !form.params) {
      toast.error("Package name and parameters are required.");
      return;
    }
    const payload = {
      slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
      name: form.name,
      desc: form.desc,
      params: form.params,
      size: form.size,
      tag: form.tag || null,
      image: form.image || null,
      order: Number(form.order) || 0,
    };
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/packages/${initial.id}`, payload);
      else await api.post("/packages", payload);
      toast.success(initial?.id ? "Package updated." : "Package added.");
      onSaved();
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-navy/10 p-6 lg:p-8 grid sm:grid-cols-2 gap-6" data-testid="package-form">
      <div>
        <label className={labelClass}>Package Name</label>
        <input data-testid="package-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={field} />
      </div>
      <div>
        <label className={labelClass}>Parameters Label</label>
        <input data-testid="package-params-input" value={form.params} onChange={(e) => set("params", e.target.value)} className={field} placeholder="72 Parameters" />
      </div>
      <div className="sm:col-span-2">
        <label className={labelClass}>Description</label>
        <textarea data-testid="package-desc-input" rows={2} value={form.desc} onChange={(e) => set("desc", e.target.value)} className={`${field} resize-none`} />
      </div>
      <div>
        <label className={labelClass}>Card Size</label>
        <select data-testid="package-size-select" value={form.size} onChange={(e) => set("size", e.target.value)} className={field}>
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Tag (optional)</label>
        <input data-testid="package-tag-input" value={form.tag} onChange={(e) => set("tag", e.target.value)} className={field} placeholder="Most Booked" />
      </div>
      <div>
        <label className={labelClass}>Background Image URL (optional)</label>
        <input data-testid="package-image-input" value={form.image} onChange={(e) => set("image", e.target.value)} className={field} />
      </div>
      <div>
        <label className={labelClass}>Display Order</label>
        <input data-testid="package-order-input" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={field} />
      </div>
      <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
        <button
          onClick={save}
          disabled={saving}
          data-testid="package-save-button"
          className="btn-sheen inline-flex items-center gap-2 bg-teal text-white px-6 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-navy transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button
          onClick={onCancel}
          data-testid="package-cancel-button"
          className="inline-flex items-center gap-2 border border-navy/20 text-navy px-6 py-3 text-[11px] uppercase tracking-[0.15em] hover:border-navy transition-colors"
        >
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );
};

export const PackagesAdmin = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/packages");
      setPackages(data);
    } catch (err) {
      toast.error("Could not load packages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the website?`)) return;
    try {
      await api.delete(`/packages/${p.id}`);
      toast.success("Package removed.");
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-packages-panel">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-teal text-[10px] uppercase tracking-[0.3em] font-semibold">Health Packages</p>
          <h2 className="font-display text-3xl text-navy mt-1">Packages shown on your website</h2>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing({})}
            data-testid="admin-add-package-button"
            className="btn-sheen inline-flex items-center gap-2 bg-navy text-white px-5 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-teal transition-colors"
          >
            <Plus size={14} /> Add Package
          </button>
        )}
      </div>

      {editing && (
        <div className="mb-10">
          <PackageForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        </div>
      )}

      {loading ? (
        <Loader2 className="animate-spin text-teal" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((p, i) => (
            <div key={p.id} className="bg-white border border-navy/10 p-6" data-testid={`admin-package-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl text-navy leading-snug">{p.name}</h3>
                {p.tag && (
                  <span className="text-[9px] uppercase tracking-[0.2em] text-teal border border-teal/40 px-2 py-1 whitespace-nowrap">
                    {p.tag}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-3">{p.desc}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-teal mt-4">{p.params}</p>
              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => setEditing(p)}
                  data-testid={`admin-edit-package-${i}`}
                  className="flex-1 border border-navy text-navy py-2 text-[11px] uppercase tracking-[0.15em] hover:bg-navy hover:text-white transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`admin-delete-package-${i}`}
                  className="border border-red-200 text-red-600 px-3 py-2 hover:bg-red-600 hover:text-white transition-colors"
                  aria-label={`Delete ${p.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
